import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useSiteTexts } from "@/hooks/useSiteTexts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  SocialBubbles,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
} from "@/components/SocialLinks";
import {
  Heart,
  Phone,
  Mail,
  Loader2,
  CheckCircle2,
  Send,
  ChevronLeft,
} from "lucide-react";

const FORMULES = [
  "Formule Essentielle",
  "Formule Complète",
  "Formule Prestige",
  "Je ne sais pas encore",
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] },
};

export default function DevisMariage() {
  const texts = useSiteTexts();
  const [searchParams] = useSearchParams();
  const prefillFormule = searchParams.get("formule");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    wedding_date: "",
    formule: FORMULES.includes(prefillFormule) ? prefillFormule : "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const formValid =
    form.first_name.trim().length >= 2 &&
    form.last_name.trim().length >= 2 &&
    form.phone.trim().length >= 6 &&
    emailValid;

  const submit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Veuillez remplir correctement le formulaire (nom, prénom, téléphone, email)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/mariage/devis", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-16 sm:px-6">
        <Card className="border-primary/40 bg-card" data-testid="wedding-quote-success">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-10 w-10 text-primary" />
              <div>
                <h1 className="font-heading text-2xl font-semibold">Demande envoyée !</h1>
                <p className="text-sm text-muted-foreground">
                  Merci {form.first_name} — votre demande de devis mariage a bien été transmise.
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
              Vous allez recevoir un <span className="font-medium text-foreground">email de confirmation</span>{" "}
              à l'adresse <span className="font-medium text-primary">{form.email}</span>. Notre équipe
              vous recontacte très rapidement pour construire avec vous la formule idéale.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-muted-foreground">En attendant, suivez-nous :</span>
              <SocialBubbles />
            </div>
            <Button asChild className="mt-6" data-testid="wedding-quote-back-home">
              <Link to="/">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Retour à l'accueil
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <motion.div {...fadeUp}>
        <Badge variant="outline" className="border-primary/40 text-primary">
          <Heart className="mr-1.5 h-3.5 w-3.5" />
          {texts.devis_badge || "Devis mariage"}
        </Badge>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          {texts.devis_title_a || "Parlons de votre"}{" "}
          <span className="text-primary">{texts.devis_title_accent || "grand jour"}</span>
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {texts.devis_subtitle ||
            "Chaque mariage est unique : contactez-nous et nous construisons ensemble la formule parfaite. Devis personnalisé gratuit et sans engagement."}
        </p>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Contacts prioritaires */}
        <motion.div {...fadeUp} className="space-y-4 lg:col-span-5">
          <Card className="border-primary/50 bg-card" data-testid="wedding-contact-phone">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary">
                  <Phone className="h-5 w-5 text-primary-foreground" />
                </span>
                <Badge className="bg-primary text-primary-foreground">En priorité</Badge>
              </div>
              <h2 className="mt-4 font-heading text-lg font-semibold">Appelez-nous directement</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Le plus rapide pour parler de votre projet de vive voix.
              </p>
              {CONTACT_PHONE ? (
                <a
                  href={CONTACT_PHONE_HREF}
                  className="mt-3 inline-block font-mono-nums text-2xl font-semibold text-primary underline-offset-4 hover:underline"
                  data-testid="wedding-contact-phone-link"
                >
                  {CONTACT_PHONE}
                </a>
              ) : (
                <p className="mt-3 font-heading text-base font-semibold text-primary" data-testid="wedding-contact-phone-soon">
                  Numéro bientôt disponible
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border bg-card" data-testid="wedding-contact-socials">
            <CardContent className="p-5 sm:p-6">
              <h2 className="font-heading text-lg font-semibold">Sur nos réseaux sociaux</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Écrivez-nous en message privé, nous répondons rapidement.
              </p>
              <SocialBubbles size="lg" className="mt-4" />
            </CardContent>
          </Card>

          <Card className="border-border bg-card" data-testid="wedding-contact-email">
            <CardContent className="p-5 sm:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
                <Mail className="h-5 w-5 text-primary" />
                Par email
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Ou utilisez directement le formulaire ci-contre — vous recevrez une confirmation par
                email.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="mt-3 inline-block font-medium text-primary underline-offset-2 hover:underline"
                data-testid="wedding-contact-email-link"
              >
                {CONTACT_EMAIL}
              </a>
            </CardContent>
          </Card>
        </motion.div>

        {/* Formulaire */}
        <motion.div {...fadeUp} className="lg:col-span-7">
          <Card className="border-border bg-card" data-testid="wedding-quote-form-card">
            <CardContent className="p-5 sm:p-7">
              <h2 className="font-heading text-lg font-semibold">Demande de devis en ligne</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Laissez-nous vos coordonnées : nous recevons votre demande immédiatement et vous
                recevez une confirmation par email.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wq-first-name">Prénom *</Label>
                    <Input
                      id="wq-first-name"
                      placeholder="Sarah"
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      data-testid="wedding-quote-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wq-last-name">Nom *</Label>
                    <Input
                      id="wq-last-name"
                      placeholder="Benali"
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      data-testid="wedding-quote-last-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wq-phone">Téléphone *</Label>
                    <Input
                      id="wq-phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="wedding-quote-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wq-email">Email *</Label>
                    <Input
                      id="wq-email"
                      type="email"
                      placeholder="sarah@exemple.fr"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="wedding-quote-email"
                    />
                    {form.email && !emailValid && (
                      <p className="text-xs text-destructive">Adresse email invalide</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wq-date">Date du mariage (si connue)</Label>
                    <Input
                      id="wq-date"
                      type="date"
                      value={form.wedding_date}
                      onChange={(e) => setForm({ ...form, wedding_date: e.target.value })}
                      data-testid="wedding-quote-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Formule souhaitée</Label>
                    <Select
                      value={form.formule}
                      onValueChange={(v) => setForm({ ...form, formule: v })}
                    >
                      <SelectTrigger data-testid="wedding-quote-formule">
                        <SelectValue placeholder="Choisir une formule" />
                      </SelectTrigger>
                      <SelectContent>
                        {FORMULES.map((f) => (
                          <SelectItem key={f} value={f}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wq-message">Votre projet (lieu, nombre d'invités, envies…)</Label>
                  <Textarea
                    id="wq-message"
                    rows={4}
                    placeholder="Parlez-nous de votre journée : lieu de réception, nombre d'invités, ambiance souhaitée…"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="wedding-quote-message"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={submitting || !formValid}
                  data-testid="wedding-quote-submit"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Envoyer ma demande de devis
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  * Champs obligatoires. Vos coordonnées ne sont utilisées que pour vous recontacter
                  au sujet de votre demande.
                </p>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
